import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  ButtonGroup,
} from "@chakra-ui/react";
import { MAXIMUM_STRIPE_AMOUNT } from "@equidefi/shared/constants/payments";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";

import { CurrencyFormatter } from "@/constants/format";
import { PAYMENT_CATEGORY_MAP } from "@/constants/payments";
import { stripeAccountPromise, stripePromise } from "@/constants/stripe";
import {
  useCancelUncompletePayments,
  useInitiatePaymentMutation,
  useSetupManualInvestmentPayment,
} from "@/hooks/useInvestments";
import useWorkflowPosition from "@/hooks/useWorkflowPosition";
import { useWorkflowContext } from "@/pages/workflow/context/WorkflowContext";
import { useDocumentTitle } from "@equidefi/portals/hooks/useDocumentTitle";
import { Heading, Text } from "@equidefi/ui";

import { WorkflowContainer } from "../WorkflowContainer";
import WorkflowStepError from "../WorkflowStepError";
import { PaymentAccordion } from "./PaymentAccordion";
import { PaymentStepContext } from "./context";
import { WorkflowButton } from "@/components/workflow/WorkflowButton";
import ReviewCard from "@/components/workflow/ReviewCard";
import { IssuerNameAlert } from "@/components/investment/IssuerNameAlert";
import { formatSecurities } from "@equidefi/shared";

export const WorkflowPayment = () => {
  const { investment, offering, analytics, refetchInvestment } =
    useWorkflowContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [changePayment, setChangePayment] = useState(false);
  const cancelPayments = useCancelUncompletePayments(investment?.id);

  useWorkflowPosition(3);

  useEffect(() => {
    refetchInvestment();
  }, [refetchInvestment]);

  const setupManualPayment = useSetupManualInvestmentPayment(investment?.id);

  const settingsMap = useMemo(() => {
    const transferAgent = offering?.offering_contacts?.find(
      (c) => c.type === "TRANSFER_AGENT",
    );
    return {
      bank_name: offering?.payment_bank_name,
      account_name: offering?.payment_account_name,
      account_number: offering?.payment_account_number,
      routing_number: offering?.payment_routing_number,
      transfer_agent_name: transferAgent?.company,
      wireInstructions: offering?.manual_payment_wire_instructions,
      checkInstructions: offering?.manual_payment_check_instructions,
    };
  }, [offering]);

  const createPayment = useInitiatePaymentMutation(investment?.id);

  useDocumentTitle([
    "Payment",
    investment?.offering?.name,
    investment?.issuer?.name,
  ]);

  const [paymentCategory, setPaymentCategory] = useState(null);

  const [clientSecret, setClientSecret] = useState("");
  const [paymentIntentId, setPaymentIntentId] = useState("");
  const [stripe, setStripe] = useState(null);

  const handleSectionChange = (index) => {
    const type = PAYMENT_CATEGORY_MAP[index];
    setPaymentCategory(type);
    if (type === "STRIPE") setupStripePayment();
  };

  const setupStripePayment = async () => {
    if (clientSecret) return null;

    try {
      const clientSecretData = await createPayment.mutateAsync();

      if (offering.stripe_account_id) {
        setStripe(stripeAccountPromise(offering.stripe_account_id));
      } else {
        setStripe(stripePromise);
      }
      setClientSecret(clientSecretData.clientSecret);
      setPaymentIntentId(clientSecretData.id);
    } catch (e) {
      console.error(e);
    }
  };

  const handlePayment = async () => {
    analytics.track("payment", { type: "manual" });

    let page = "address";

    if (location.state?.returnTo) {
      page = location.state.returnTo;
    }

    try {
      await setupManualPayment.mutateAsync({
        category: "MANUAL",
        payment_type: paymentCategory.toLowerCase(),
      });

      refetchInvestment();

      if (investment.expedited) {
        return navigate(
          `/offerings/${offering.slug}/${investment?.id}/agreement`,
        );
      }

      navigate(`/offerings/${offering.slug}/${investment?.id}/${page}`);
    } catch (e) {
      console.error(e);
    }
  };

  const supportsStripePayment = investment?.amount <= MAXIMUM_STRIPE_AMOUNT;

  if (investment?.payment_action_url && !changePayment) {
    return (
      <WorkflowContainer title="Payment">
        <Alert
          status="info"
          borderRadius="lg"
          border="1px solid"
          borderColor="equidefi.blue"
          boxShadow="sm"
          my={4}>
          <AlertIcon />
          <Text textStyle="body2" m={0}>
            Your chosen payment option requires confirmation. Please continue to
            submit your payment.
          </Text>
        </Alert>

        <ButtonGroup orientation="vertical" w="100%">
          <WorkflowButton
            onClick={() => {
              window.location.href = investment?.payment_action_url;
            }}>
            Continue
          </WorkflowButton>

          <Button
            onClick={async () => {
              await cancelPayments.mutateAsync();
              setChangePayment(true);
            }}
            variant="outline">
            Change Payment Method
          </Button>
        </ButtonGroup>
      </WorkflowContainer>
    );
  }

  if (!investment?.payment_editable && !changePayment) {
    return (
      <WorkflowStepError
        onResume={() =>
          navigate(`/offerings/${offering.slug}/${investment.id}/${page}`)
        }
        title="Payment"
        text="Your payment has already been submitted. You can check the status of your payment in your portfolio."
      />
    );
  }

  return (
    <PaymentStepContext.Provider
      value={{
        paymentIntentId,
        clientSecret,
      }}>
      <WorkflowContainer title="Payment" buttonLabel="Pay">
        <Box>
          <IssuerNameAlert name={offering?.issuer?.name} />
          <ReviewCard
            title="Investment Amount"
            page="subscription"
            returnTo="payment">
            <Text fontWeight="bold" mb={0}>
              {CurrencyFormatter.format(investment.amount)}
            </Text>
            <Text mb={0}>
              {formatSecurities({
                quantity: investment?.quantity,
                offering,
              }).toString()}
            </Text>
          </ReviewCard>
          <Alert
            status="info"
            borderRadius="lg"
            border="1px solid"
            borderColor="equidefi.blue"
            boxShadow="sm"
            my={4}>
            <AlertIcon />
            <AlertDescription>
              <Text fontWeight="bold" m="0" as="h4">
                Investment acceptance
              </Text>
              <Text textStyle="body2" m="0">
                It may take 7-10 business days for your investment to be
                accepted. If your investment is not accepted, you will be
                notified and your funds will be refunded.
              </Text>
            </AlertDescription>
          </Alert>
        </Box>

        <Heading fontWeight="bold">Select your payment method</Heading>
        <PaymentAccordion
          onSubmitManualPayment={handlePayment}
          supportsStripePayment={supportsStripePayment}
          onSectionChange={handleSectionChange}
          stripe={stripe}
          clientSecret={clientSecret}
          isManualPaymentLoading={setupManualPayment.isLoading}
          paymentSettings={settingsMap}
          investment={investment}
          offering={offering}
        />
      </WorkflowContainer>
    </PaymentStepContext.Provider>
  );
};
